import React, { useState } from 'react';
import { Container, Typography, Box } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import SearchIcon from '@mui/icons-material/Search';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import BugReportIcon from '@mui/icons-material/BugReport';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import UpdateIcon from '@mui/icons-material/Update';
import './MobileProcess.css';

const processes = [
  {
    icon: <SearchIcon sx={{ fontSize: 40 }} />,
    title: 'Discovery & Planning',
    description: 'We analyze your requirements, target audience, and business goals to create a comprehensive mobile app strategy.',
    color: '#FF6B6B',
    gradient: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E8E 100%)',
    highlights: ['Requirements Analysis', 'Market Research', 'Project Roadmap']
  },
  {
    icon: <DesignServicesIcon sx={{ fontSize: 40 }} />,
    title: 'UI/UX Design',
    description: 'Our designers create intuitive, engaging interfaces that provide exceptional user experiences across all devices.',
    color: '#4ECDC4',
    gradient: 'linear-gradient(135deg, #4ECDC4 0%, #2EAF9F 100%)',
    highlights: ['Wireframing', 'Visual Design', 'Prototyping']
  },
  {
    icon: <DeveloperModeIcon sx={{ fontSize: 40 }} />,
    title: 'Development',
    description: 'We build your app using the latest technologies and best practices, ensuring high performance and scalability.',
    color: '#45B7D1',
    gradient: 'linear-gradient(135deg, #45B7D1 0%, #2E97B7 100%)',
    highlights: ['Clean Code', 'Best Practices', 'Performance Optimization']
  },
  {
    icon: <BugReportIcon sx={{ fontSize: 40 }} />,
    title: 'Testing & QA',
    description: 'Rigorous testing across multiple devices and platforms to ensure your app works flawlessly.',
    color: '#96CEB4',
    gradient: 'linear-gradient(135deg, #96CEB4 0%, #6BAF96 100%)',
    highlights: ['Unit Testing', 'Integration Testing', 'User Testing']
  },
  {
    icon: <RocketLaunchIcon sx={{ fontSize: 40 }} />,
    title: 'Deployment',
    description: 'We handle the app store submission process and ensure a smooth launch of your application.',
    color: '#4B7BE5',
    gradient: 'linear-gradient(135deg, #4B7BE5 0%, #3153B0 100%)',
    highlights: ['App Store Optimization', 'Launch Strategy', 'Marketing Support']
  },
  {
    icon: <UpdateIcon sx={{ fontSize: 40 }} />,
    title: 'Support & Updates',
    description: 'Continuous monitoring, maintenance, and updates to keep your app running at peak performance.',
    color: '#6C63FF',
    gradient: 'linear-gradient(135deg, #6C63FF 0%, #4B44CC 100%)',
    highlights: ['24/7 Monitoring', 'Regular Updates', 'Performance Analytics']
  }
];

const MobileProcess = () => {
  const [activeStep, setActiveStep] = useState(null);

  return (
    <section className="mobile-process">
      <div className="process-background">
        <div className="process-sphere"></div>
        <div className="process-sphere-2"></div>
      </div>
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="process-header"
        >
          <Typography variant="h2" className="ma-section-title">
            Our Development Process
          </Typography>
          <Typography variant="subtitle1" className="ma-section-subtitle">
            A streamlined approach to bringing your mobile app to life
          </Typography>
        </motion.div>

        <Box className="process-timeline">
          {processes.map((process, index) => (
            <motion.div
              key={index}
              className={`process-item ${activeStep === index ? 'active' : ''}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              onHoverStart={() => setActiveStep(index)}
              onHoverEnd={() => setActiveStep(null)}
            >
              <div className="process-content">
                <motion.div 
                  className="process-icon-wrapper"
                  style={{ background: process.gradient }}
                  whileHover={{ 
                    scale: 1.05,
                    rotate: 5,
                    boxShadow: '0 8px 30px rgba(0,0,0,0.2)'
                  }}
                >
                  <motion.div 
                    className="process-icon"
                    // animate={{ rotate: activeStep === index ? 360 : 0 }}
                    // transition={{ duration: 0.5 }}
                  >
                    {process.icon}
                  </motion.div>
                  <div className="process-step">Step {index + 1}</div>
                </motion.div>
                <div className="process-text">
                  <Typography variant="h5" className="process-title">
                    {process.title}
                  </Typography>
                  <Typography className="process-description">
                    {process.description}
                  </Typography>
                  <AnimatePresence>
                    {activeStep === index && (
                      <motion.div
                        className="process-highlights"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        {process.highlights.map((highlight, i) => (
                          <motion.div
                            key={i}
                            className="highlight-item"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.1 }}
                          >
                            <span className="highlight-dot" style={{ background: process.color }}></span>
                            {highlight}
                          </motion.div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                {index !== processes.length - 1 && (
                  <div className="process-connector">
                    <motion.div
                      className="connector-line"
                      initial={{ height: 0 }}
                      whileInView={{ height: '100%' }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
                      style={{
                        background: `linear-gradient(to bottom, ${process.color}, ${processes[index + 1].color})`
                      }}
                    />
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </Box>
      </Container>
    </section>
  );
};

export default MobileProcess; 